import React, { useState, useRef, useEffect } from 'react';
import { Product, ProductType } from '../types';
import { XMarkIcon, ChevronDownIcon } from '../constants';

interface SingleProductSelectionInputProps {
  allProducts: Product[];
  selectedProductId: string;
  onChange: (selectedProductId: string) => void;
  label: string;
  inputId: string;
  productType?: ProductType; // Filter products by type
  error?: string;
  required?: boolean;
}

const SingleProductSelectionInput: React.FC<SingleProductSelectionInputProps> = ({
  allProducts,
  selectedProductId,
  onChange,
  label,
  inputId,
  productType,
  error,
  required = false,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelectProduct = (productId: string) => {
    onChange(productId);
    setShowDropdown(false);
    setSearchTerm('');
  };

  const handleToggleDropdown = () => {
    if (!showDropdown && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // If there's not enough space below (less than 250px for dropdown), flip it up
      setDropdownDirection(spaceBelow < 250 && spaceAbove > 250 ? 'up' : 'down');
    }
    setShowDropdown(!showDropdown);
  };

  const handleClearSelection = () => {
    onChange('');
  };

  const filteredProducts = allProducts.filter(p => {
    const matchesType = !productType || p.productType === productType;
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         p.productId.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });


  const selectedProduct = allProducts.find(p => p.id === selectedProductId);

  return (
    <div className="mb-4">
      <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          id={inputId}
          className={`w-full flex justify-between items-center px-3 py-2 border rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${error ? 'border-red-500' : 'border-slate-300'}`}
          onClick={handleToggleDropdown}
          aria-haspopup="listbox"
          aria-expanded={showDropdown}
          aria-required={required}
          aria-describedby={error ? `${inputId}-error` : undefined}
        >
          <span className="block truncate">
            {selectedProduct ? `${selectedProduct.name} (${selectedProduct.productId})` : 'Select a product...'}
          </span>
          <div className="flex items-center">
            {selectedProduct && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearSelection();
                }}
                className="mr-2 text-slate-400 hover:text-slate-600 cursor-pointer"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    e.preventDefault();
                    handleClearSelection();
                  }
                }}
                aria-label="Clear selection"
              >
                <XMarkIcon className="w-4 h-4" />
              </div>
            )}
            <ChevronDownIcon className={`w-5 h-5 text-slate-400 transition-transform ${showDropdown ? 'transform rotate-180' : ''}`} />
          </div>
        </button>

        {showDropdown && (
          <div className={`absolute z-50 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm ${
            dropdownDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}>
            <input
              type="text"
              placeholder="Search products..."
              className="w-full px-3 py-2 border-b border-slate-200 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {filteredProducts.length > 0 ? (
              filteredProducts.map(product => (
                <div
                  key={product.id}
                  onClick={() => handleSelectProduct(product.id)}
                  className={`cursor-pointer select-none relative py-2 pl-3 pr-9 text-slate-900 hover:bg-indigo-100 ${selectedProductId === product.id ? 'bg-indigo-50' : ''}`}
                >
                  <span className="block truncate">{product.name} ({product.productId})</span>
                  {productType && (
                    <span className="text-xs text-slate-500">
                      {product.productType === ProductType.INFEED ? 'Infeed' : 'Outfeed'}
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-slate-500">No products available or match search.</div>
            )}
          </div>
        )}
      </div>

      {error && <p id={`${inputId}-error`} className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default SingleProductSelectionInput;